import { Alert, Fade, Stack, Tooltip, Typography } from '@mui/material';
import Grid from '@mui/material/Grid2';
import { Add, Cancel, Deck, FormatPaint, Place, ViewQuilt } from '@mui/icons-material';
import Svg from 'react-inlinesvg';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  IconLabel,
  GeneralIconLabel,
  GeneralInfo,
  GeneralLabel,
  GeneralPOIInfoPanel,
  GeneralValue,
  iconStyles,
  POIDetails,
  POIMedia,
  SplitContainer,
  DetailBox,
  DetailLabel,
  DetailValue,
  ImageFrame,
  RemoveOverlay,
  ImageUploadPanel,
  UploadButton,
  ActionButton,
  ActionButtonLabel,
  DetailHeading,
} from './styles';
import { MappedReportPointOfInterests } from '@/types';
import {
  downloadFile,
  formatEnumForDisplay,
  formatFieldValue,
  truncateFilenameFromMiddle,
  urlToBase64,
} from '@/utils';
import * as state from '@/components/Analysis/state';
import { getCloudfrontUrl } from '@/utils/cloudfront';
import { PointOfInterestDeckInfo } from './PointOfInterestDeckInfo';
import { AddImageIcon } from '@/assets/icons/AddImageIcon';
import { abyssColors } from '@/theme/colors';
import { ImageSelectionModal } from './ImageSelectionModal';
import {
  TemplateFieldTypeEnum,
  File,
  ReportDetailsDocument,
  useUpdatePoiImagesMutation,
  UnitSystemEnum,
  FileTypes,
  FileNameTypes,
  Vector3,
  PointOfInterestImages,
  useAllReportImagesQuery,
  UnitsEnum,
  ImageType,
  TableCellStyleEnum,
} from '@/__generated__/graphql';
import {
  NOT_APPLICABLE_LABEL,
  PIT_DEPTH_LABEL,
  UNCERTAIN_PROFILE_LABEL,
  BLISTER_HEIGHT_LABEL,
  POST_INSPECTION_LABEL,
  PRE_INSPECTION_LABEL,
  BLISTER_TYPE,
  UNCERTAIN_PROFILE_SHORT_LABEL,
} from '@/constants';
import useFetchTemplatesByType from '@/hooks/useFetchTemplatesByType';
import { useBlisterFieldConfig } from '@/hooks/useBlisterFieldConfig';
import {
  convertInchesToMeters,
  convertMillimetersToMeters,
  formatPercentage,
  localiseMeasurement,
} from '@/utils/unitSystem';
import { getDeckDetails, getPoiCoordinates } from '@/utils/pointOfInterest';
import { AbyssIcon } from '@/assets/icons/AbyssIcon';
import { generatePoiDisplayName } from '@/components/Analysis/modules/pointOfInterest/PointOfInterestTab/SelectedPoiContent/utils';
import { MediaViewer } from '@/components/shared/MediaViewer';
import { Image } from '@/components/Analysis/shared/Image';
import { Attachment } from '@/components/shared/AttachmentDisplay/type';
import { useGetStructureId } from '@/hooks/useGetStructureId';
import { useStructureForReportLazyQuery } from '@/__generated__/graphql';
import { NoteSection } from '@/components/shared/NoteSection/NoteSection';
import { EditNoteDialog } from '@/components/shared/NoteSection/EditNoteDialog';
import { usePoiRemediationNoteMutation } from '@/hooks/usePoiRemediationNoteMutation';
import { useSnackBarMessage } from '@/utils/useSnackBarMessage';

type POIInfoProps = {
  data: MappedReportPointOfInterests;
};

type DeckImage = {
  resourcePath: string;
  imageDimensions: number[];
  cadTransform: number[];
  centerPoint3d?: Vector3 | null;
};

type RemovableImageProps = {
  deckImage?: DeckImage;
  image: File;
  onRemove: (event: React.MouseEvent<HTMLButtonElement>) => void;
  setAttachments: React.Dispatch<React.SetStateAction<Attachment[]>>;
};

const showError = (message: string) => {
  return (
    <div>
      <Alert severity="error">{message}</Alert>
    </div>
  );
};

const showSuccess = (message: string) => {
  return <Alert severity="success">{message}</Alert>;
};

export const GeneralInfoPanel = ({ data }: POIInfoProps) => {
  const structureId = useGetStructureId();

  const [getStructureForReport, { data: structureData }] = useStructureForReportLazyQuery({
    fetchPolicy: 'cache-first',
  });

  useEffect(() => {
    if (structureId) {
      getStructureForReport({ variables: { structureId } });
    }
  }, [getStructureForReport, structureId]);

  const poiReportConfig = useMemo(() => {
    return structureData?.structure?.reportConfig?.configurations ?? [];
  }, [structureData?.structure?.reportConfig]);

  const visibleKeys = useMemo(() => {
    const keys = poiReportConfig.map((config) => config?.key) ?? [];

    return new Set(keys);
  }, [poiReportConfig]);

  const infoList = React.useMemo(() => {
    return [
      {
        label: poiReportConfig.find((config) => config?.key === 'poi-name')?.title ?? 'Name / ID',
        value:
          generatePoiDisplayName({ name: data.name, poiType: data.type, poiId: data.poiId }) ??
          NOT_APPLICABLE_LABEL,
        testId: 'poi-name',
        visibility: visibleKeys.has('poi-name'),
      },
      {
        label:
          poiReportConfig.find((config) => config?.key === 'workpack-number')?.title ?? 'Workpack',
        value: data.workpack?.name ?? NOT_APPLICABLE_LABEL,
        testId: 'poi-workpack',
        visibility: visibleKeys.has('workpack-number'),
      },
      {
        label:
          poiReportConfig.find((config) => config?.key === 'is-verified')?.title ?? 'POI verified',
        value: data.isVerified ? 'Yes' : 'No',
        icon: <AbyssIcon iconName="verify-doc" style={{ height: '1.6rem' }} />,
        testId: 'poi-verified',
        color: data.isVerified ? abyssColors.success[600] : abyssColors.error[600],
        visibility: visibleKeys.has('is-verified'),
      },
      {
        label: poiReportConfig.find((config) => config?.key === 'poi-type')?.title ?? 'POI type',
        value: data.poiTemplateName ?? formatEnumForDisplay(data.type),
        icon: <FormatPaint sx={iconStyles} />,
        testId: 'poi-type',
        visibility: visibleKeys.has('poi-type'),
      },
      {
        label:
          poiReportConfig.find((config) => config?.key === 'paint-block')?.title ?? 'Paint Block',
        value: data.paintRegion?.name ?? NOT_APPLICABLE_LABEL,
        icon: <ViewQuilt sx={iconStyles} />,
        testId: 'poi-block',
        visibility: visibleKeys.has('paint-block'),
      },
      {
        label: poiReportConfig.find((config) => config?.key === 'deck')?.title ?? 'POI Deck',
        value: data.paintRegion?.deck.name ?? NOT_APPLICABLE_LABEL,
        icon: <Deck sx={iconStyles} />,
        testId: 'poi-deck',
        visibility: visibleKeys.has('deck'),
      },
      {
        label:
          poiReportConfig.find((config) => config?.key === 'accessibility')?.title ??
          'Accessibility',
        value: formatEnumForDisplay(data.accessibility ?? NOT_APPLICABLE_LABEL),
        icon: <Place sx={iconStyles} />,
        testId: 'poi-accessibility',
        visibility: visibleKeys.has('accessibility'),
      },
    ];
  }, [
    data.accessibility,
    data.poiTemplateName,
    data.isVerified,
    data.name,
    data.paintRegion?.deck.name,
    data.paintRegion?.name,
    data.poiId,
    data.type,
    data.workpack?.name,
    poiReportConfig,
    visibleKeys,
  ]);

  return (
    <GeneralPOIInfoPanel data-testId="general-info-panel">
      {infoList
        .filter((info) => {
          // fallback - if config is empty, skip filtering
          return visibleKeys.size === 0 || info.visibility;
        })
        .map((info) => (
          <GeneralInfo key={info.label}>
            {info.icon ? (
              <IconLabel>
                {info.icon}
                <GeneralIconLabel>{info.label}</GeneralIconLabel>
              </IconLabel>
            ) : (
              <GeneralLabel>{info.label}</GeneralLabel>
            )}
            <GeneralValue
              data-testId={info.testId}
              {...(info.color ? { customColor: info.color } : {})}
            >
              {info.value}
            </GeneralValue>
          </GeneralInfo>
        ))}
    </GeneralPOIInfoPanel>
  );
};

const RemovableReportImage = ({
  deckImage,
  image,
  onRemove,
  setAttachments,
}: RemovableImageProps) => {
  const [isHovered, setHovered] = useState<boolean>(false);
  const [hasError, setHasError] = useState<boolean>(false);
  const isPdf = image.type === FileTypes.Pdf;
  const handleClick = useCallback(
    (e: React.MouseEvent) => {
      if (hasError) {
        e.stopPropagation();
        e.preventDefault();
      }
    },
    [hasError]
  );

  const handleError = useCallback(
    (success: boolean) => {
      setHasError(!success);
      if (!success) {
        setAttachments((previous) => {
          return previous.filter((attachment) => !attachment.url?.includes(image.resourcePath));
        });
      }
    },
    [image.resourcePath, setAttachments]
  );

  return (
    <ImageFrame
      onMouseEnter={() => {
        !hasError && setHovered(true);
      }}
      onMouseLeave={() => {
        !hasError && setHovered(false);
      }}
      onClick={handleClick}
      sx={{
        cursor: hasError ? 'not-allowed' : 'pointer',
        pointerEvents: hasError ? 'all' : 'auto',
      }}
    >
      {deckImage ? (
        <PointOfInterestDeckInfo
          resourcePath={deckImage?.resourcePath}
          imageDimensions={deckImage?.imageDimensions}
          cadTransform={deckImage?.cadTransform}
          centerPoint3d={deckImage?.centerPoint3d}
          handleError={handleError}
        />
      ) : isPdf ? (
        <Stack justifyContent="center" alignItems="center" sx={{ width: '100%', height: '100%' }}>
          <Svg
            style={{
              width: '10%',
              height: '10%',
            }}
            src="/assets/pdf_logo.svg"
          />
        </Stack>
      ) : (
        <Image
          src={getCloudfrontUrl(image?.resourcePath ?? '')}
          alt={image?.name}
          onLoadEnd={handleError}
          imgSx={{ width: '100%', height: '100%', objectFit: 'cover' }}
        />
      )}
      <Fade in={isHovered && !hasError}>
        <RemoveOverlay>
          <ActionButton
            variant="outlined"
            onClick={(e) => {
              if (!hasError) {
                onRemove(e);
              }
            }}
          >
            <Cancel htmlColor={abyssColors.neutral[50]} sx={{ fontSize: '1.4rem' }} />
            <ActionButtonLabel>Remove</ActionButtonLabel>
          </ActionButton>
        </RemoveOverlay>
      </Fade>
    </ImageFrame>
  );
};

export const DetailedInfoSection = ({ data }: POIInfoProps) => {
  const { data: allPointOfInterestTemplates } = useFetchTemplatesByType();
  const setSnackbarMessage = useSetAtom(state.snackbarMessage);
  const [selectedReport, setSelectedReport] = useAtom(state.selectedReport);
  const unitConversionSystem = useAtomValue(state.unitSystem);
  const blisterFieldConfig = useBlisterFieldConfig({ type: data.type });
  const [uploadModalOpen, setUploadModalOpen] = React.useState<boolean>(false);
  const [editNoteDialogOpen, setEditNoteDialogOpen] = React.useState<boolean>(false);
  const [attachments, setAttachments] = useState<Attachment[]>([]);
  const { data: allReportImagesData } = useAllReportImagesQuery({
    variables: { reportId: selectedReport?.id ?? '' },
    skip: !selectedReport?.id,
  });
  const { updateNote } = usePoiRemediationNoteMutation();
  const { showSnackBar } = useSnackBarMessage({});

  const templateOfSelectedPoi = useMemo(() => {
    return allPointOfInterestTemplates?.allPointOfInterestTemplate.find(
      ({ id }) => id === data.template?.id
    );
  }, [allPointOfInterestTemplates, data]);

  const templateFields = useMemo(() => {
    return (
      templateOfSelectedPoi?.fields?.filter(
        (field) =>
          !['comments', 'comment'].includes(field.name.toLowerCase()) && field.displayInReports
      ) || []
    );
  }, [templateOfSelectedPoi]);

  const templateFieldsWithValues: Array<{
    label: string;
    value: string;
    isLink?: boolean;
  }> = useMemo(() => {
    if (data.blister) {
      const { maxHeight, wallLoss, residualCorrosionAllowance, residualThickness } =
        data.blister || {};
      const label =
        blisterFieldConfig.maxHeight?.name ||
        (data.type === BLISTER_TYPE ? BLISTER_HEIGHT_LABEL : PIT_DEPTH_LABEL);
      const blisterFields = [
        // Max Height field
        ...(!blisterFieldConfig.maxHeight?.hidden
          ? [
              {
                value: localiseMeasurement({
                  unitSystem: unitConversionSystem,
                  value: maxHeight || undefined,
                  showUnitLabel: true,
                  sourceUnit: UnitsEnum.Meter,
                  displayAsUnit: UnitsEnum.Millimeter,
                  fallbackLabel: UNCERTAIN_PROFILE_SHORT_LABEL,
                }),
                label: label,
              },
            ]
          : []),
        // Wall Loss field
        ...(!blisterFieldConfig.wallLoss?.hidden
          ? [
              {
                value:
                  wallLoss?.value === null || wallLoss?.value === undefined
                    ? NOT_APPLICABLE_LABEL
                    : localiseMeasurement({
                        unitSystem: unitConversionSystem,
                        value: wallLoss.value,
                        showUnitLabel: true,
                        sourceUnit: UnitsEnum.Meter,
                        displayAsUnit: UnitsEnum.Millimeter,
                      }) +
                      (wallLoss?.percentage !== null && wallLoss?.percentage !== undefined
                        ? formatPercentage(wallLoss.percentage)
                        : ''),
                label: blisterFieldConfig.wallLoss?.name || 'Estimated Wall Loss',
              },
            ]
          : []),
        // Residual Corrosion Allowance field
        ...(!blisterFieldConfig.residualCorrosionAllowance?.hidden
          ? [
              {
                value:
                  residualCorrosionAllowance?.value === null ||
                  residualCorrosionAllowance?.value === undefined
                    ? NOT_APPLICABLE_LABEL
                    : localiseMeasurement({
                        unitSystem: unitConversionSystem,
                        value: residualCorrosionAllowance.value,
                        showUnitLabel: true,
                        sourceUnit: UnitsEnum.Meter,
                        displayAsUnit: UnitsEnum.Millimeter,
                      }) +
                      (residualCorrosionAllowance?.percentage !== null &&
                      residualCorrosionAllowance?.percentage !== undefined
                        ? formatPercentage(residualCorrosionAllowance.percentage)
                        : ''),
                label:
                  blisterFieldConfig.residualCorrosionAllowance?.name ||
                  'Residual Corrosion Allowance',
              },
            ]
          : []),
        // Residual Thickness field
        ...(!blisterFieldConfig.residualThickness?.hidden
          ? [
              {
                value:
                  residualThickness?.value === null || residualThickness?.value === undefined
                    ? NOT_APPLICABLE_LABEL
                    : localiseMeasurement({
                        unitSystem: unitConversionSystem,
                        value: residualThickness.value,
                        showUnitLabel: true,
                        sourceUnit: UnitsEnum.Meter,
                        displayAsUnit: UnitsEnum.Millimeter,
                      }) +
                      (residualThickness?.percentage !== null &&
                      residualThickness?.percentage !== undefined
                        ? formatPercentage(residualThickness.percentage)
                        : ''),
                label: blisterFieldConfig.residualThickness?.name || 'Residual Thickness',
              },
            ]
          : []),
        ...(!blisterFieldConfig.measurementConfidence?.hidden
          ? [
              {
                value: data.blister.measurementConfidence ?? NOT_APPLICABLE_LABEL,
                label: blisterFieldConfig.measurementConfidence?.name || 'Measurement Confidence',
              },
            ]
          : []),
        ...(!blisterFieldConfig.distanceConfidence?.hidden
          ? [
              {
                value: data.blister.distanceConfidence ?? NOT_APPLICABLE_LABEL,
                label: blisterFieldConfig.distanceConfidence?.name || 'Distance Confidence',
              },
            ]
          : []),
      ];

      const templateFieldsData =
        templateFields?.map((field) => {
          // Only access templateFieldData if field.key exists
          const fieldKey = field.key || '';
          const fieldValue =
            typeof data.templateFieldData?.[fieldKey] === 'string'
              ? data.templateFieldData[fieldKey].trim()
              : null;
          const value = fieldValue || NOT_APPLICABLE_LABEL;

          return {
            value,
            label: field?.name || NOT_APPLICABLE_LABEL,
            type: field?.type,
            isLink: field?.displayConfig?.type === TableCellStyleEnum.Link,
          };
        }) || [];

      return [...blisterFields, ...templateFieldsData];
    }

    return templateFields?.map((field) => {
      // Only access templateFieldData if field.key exists
      const fieldKey = field.key || '';
      const fieldValue =
        typeof data.templateFieldData?.[fieldKey] === 'string'
          ? data.templateFieldData[fieldKey].trim()
          : null;
      const value = !fieldValue
        ? NOT_APPLICABLE_LABEL
        : formatFieldValue({
            value: fieldValue,
            type: field?.type,
            unit: field?.unit,
            unitSystem: unitConversionSystem,
            isBlisterField: false,
            showUnitLabel: true,
          });

      return {
        value,
        label: field?.name || NOT_APPLICABLE_LABEL,
        isLink: field?.displayConfig?.type === TableCellStyleEnum.Link,
      };
    });
  }, [
    data.blister,
    data.type,
    templateFields,
    unitConversionSystem,
    blisterFieldConfig,
    data.templateFieldData,
  ]);

  const verifiedFieldsWithValues: Array<{
    label: string;
    value: string;
    type?: TemplateFieldTypeEnum;
  }> = useMemo(() => {
    const parseVerifiedBlisterStoredMeters = (
      raw: string | number | null | undefined
    ): number | undefined => {
      if (raw === null || raw === undefined) return undefined;

      const trimmed = String(raw).trim();
      if (!trimmed) return undefined;

      if (trimmed.endsWith('"')) {
        const inches = Number(trimmed.slice(0, -1));
        return Number.isFinite(inches) ? convertInchesToMeters(inches) : undefined;
      }
      if (trimmed.endsWith('mm')) {
        const mm = Number(trimmed.slice(0, -2));
        return Number.isFinite(mm) ? convertMillimetersToMeters(mm) : undefined;
      }
      const n = Number(trimmed);
      return Number.isFinite(n) ? n : undefined;
    };

    const blisterVerifiedRows: Array<{
      label: string;
      value: string;
      type?: TemplateFieldTypeEnum;
    }> = [];

    if (data?.blister?.verifiedMaxHeight && !blisterFieldConfig.verifiedMaxHeight?.hidden) {
      const meters = parseVerifiedBlisterStoredMeters(data.blister.verifiedMaxHeight);
      const verifiedHeight =
        meters === undefined
          ? UNCERTAIN_PROFILE_LABEL
          : localiseMeasurement({
              unitSystem: unitConversionSystem,
              value: meters,
              sourceUnit: UnitsEnum.Meter,
              displayAsUnit: UnitsEnum.Millimeter,
              showUnitLabel: true,
              fallbackLabel: UNCERTAIN_PROFILE_LABEL,
            }) || UNCERTAIN_PROFILE_LABEL;

      blisterVerifiedRows.push({
        label: blisterFieldConfig.verifiedMaxHeight?.name || 'Verified Max Height',
        value: verifiedHeight,
        type: TemplateFieldTypeEnum.Input,
      });
    }

    if (
      data?.blister?.verifiedResidualThickness &&
      !blisterFieldConfig.verifiedResidualThickness?.hidden
    ) {
      const meters = parseVerifiedBlisterStoredMeters(data.blister.verifiedResidualThickness);
      const verifiedThickness =
        meters === undefined
          ? NOT_APPLICABLE_LABEL
          : localiseMeasurement({
              unitSystem: unitConversionSystem,
              value: meters,
              sourceUnit: UnitsEnum.Meter,
              displayAsUnit: UnitsEnum.Millimeter,
              showUnitLabel: true,
            }) || NOT_APPLICABLE_LABEL;

      blisterVerifiedRows.push({
        label: blisterFieldConfig.verifiedResidualThickness?.name || 'Verified Residual Thickness',
        value: verifiedThickness,
        type: TemplateFieldTypeEnum.Input,
      });
    }

    const templateVerifiedRows =
      templateFields?.map((field) => {
        const fieldKey = field.key || '';
        let resolved: string | null = null;

        if (fieldKey && data.templateFieldData) {
          const verifiedFieldKey = `verified_${fieldKey}`;
          const raw = data.templateFieldData[verifiedFieldKey];
          if (typeof raw === 'string') {
            const trimmed = raw.trim();
            if (trimmed) resolved = trimmed;
          } else if (typeof raw === 'number') {
            resolved = String(raw);
          }
        }

        if (resolved === null) {
          const row =
            data.templateData?.find((td) => td.id === field.id) ??
            data.templateData?.find((td) => td.templateFieldId === field.id);
          const fromTemplateData = row?.verifiedValue;
          if (
            fromTemplateData !== null &&
            fromTemplateData !== undefined &&
            fromTemplateData !== ''
          ) {
            resolved =
              typeof fromTemplateData === 'string'
                ? fromTemplateData.trim() || null
                : String(fromTemplateData);
          }
        }

        if (resolved === null || resolved === '') {
          const baseVal =
            fieldKey && data.templateFieldData
              ? data.templateFieldData[fieldKey]
              : undefined;
          if (typeof baseVal === 'string') {
            const trimmedBase = baseVal.trim();
            if (trimmedBase) resolved = trimmedBase;
          } else if (typeof baseVal === 'number') {
            resolved = String(baseVal);
          }
        }

        const value =
          resolved !== null && resolved !== '' ? resolved : NOT_APPLICABLE_LABEL;

        return {
          value,
          label: field?.name || NOT_APPLICABLE_LABEL,
          type: field.type,
        };
      }) ?? [];

    return [...blisterVerifiedRows, ...templateVerifiedRows];
  }, [
    data?.blister?.verifiedMaxHeight,
    data?.blister?.verifiedResidualThickness,
    data.templateData,
    data.templateFieldData,
    data.paintRegion,
    templateFields,
    unitConversionSystem,
    blisterFieldConfig,
  ]);

  const { selectedImages, allImagesOfSelectedPOI } = useMemo(() => {
    const currentPoiId = data.id;
    const { selectedPointOfInterestImages } = selectedReport ?? {};
    const allPointOfInterestImages = allReportImagesData?.allReportImages ?? [];
    const getImages = (images?: (PointOfInterestImages | null)[] | null) => {
      return (
        (images
          ?.find((poi) => poi?.pointOfInterestId === currentPoiId)
          ?.selectedImages?.filter(Boolean) as File[]) ?? []
      );
    };
    return {
      selectedImages: [...getImages(selectedPointOfInterestImages)],
      allImagesOfSelectedPOI: [...getImages(allPointOfInterestImages)],
    };
  }, [allReportImagesData?.allReportImages, data.id, selectedReport]);

  const { selectedPdfs, allPdfsOfSelectedPOI } = useMemo(() => {
    const currentPoiId = data.id;
    const poiPdfEntry = selectedReport?.allPointOfInterestPdfs?.find(
      (pdf) => pdf?.pointOfInterestId === currentPoiId
    );

    const allPdfs = poiPdfEntry?.selectedPdfs ?? [];

    return {
      allPdfsOfSelectedPOI: allPdfs.map((pdf) => pdf?.file),
      selectedPdfs: allPdfs.filter((pdf) => pdf?.file && pdf.selectedStatus).map((pdf) => pdf.file),
    };
  }, [selectedReport, data.id]);

  const [mutateSelectedImages] = useUpdatePoiImagesMutation({
    refetchQueries: [ReportDetailsDocument],
    onCompleted: (response) => {
      if (!response?.updateReportPointOfInterestImages) {
        setSnackbarMessage({
          shouldShow: true,
          content: showError('Something went wrong'),
        });
        return;
      }
      setSnackbarMessage({
        shouldShow: true,
        content: showSuccess('Image/File removed from report'),
      });
      if (selectedReport) {
        setSelectedReport({
          ...selectedReport,
          ...response?.updateReportPointOfInterestImages.selectedPointOfInterestImages,
        });
      }
    },
    onError: (errorReponse) => {
      setSnackbarMessage({
        shouldShow: true,
        content: showError(errorReponse.message),
      });
    },
  });

  const handleRemove = (resourcePath: string, uuid?: string | undefined | null) => {
    const updatedImages =
      selectedImages
        ?.filter((image) =>
          uuid
            ? uuid !== image?.uuid && image?.uuid !== `${uuid}-${ImageType.WhiteBgPdf}`
            : image?.resourcePath !== resourcePath
        )
        .map((selected) => {
          return selected
            ? {
                name: selected.name,
                fileName: selected.fileName,
                type: selected.type,
                resourcePath: selected.resourcePath,
                uuid: selected?.uuid ?? undefined,
                imageType: selected?.imageType ?? undefined,
              }
            : selected;
        }) ?? [];

    const updatedPdfs =
      selectedPdfs
        .filter((pdf) => pdf.resourcePath !== resourcePath)
        .map((selected) => {
          return selected
            ? {
                name: selected.name,
                fileName: selected.fileName,
                type: selected.type,
                resourcePath: selected.resourcePath,
              }
            : selected;
        }) ?? [];

    if (selectedReport?.id) {
      mutateSelectedImages({
        variables: {
          input: {
            reportId: selectedReport?.id,
            pointOfInterestId: data.id,
            selectedImages: updatedImages,
            selectedPdfs: updatedPdfs,
          },
        },
      });
    }
  };

  const deckDetails = useMemo(
    () => getDeckDetails(data.location?.deck ?? data.paintRegion?.deck),
    [data.location?.deck, data.paintRegion?.deck]
  );

  const location = useMemo(() => {
    const cadTransform = deckDetails?.cadTransform;
    if (!cadTransform) return;
    const { centerPoint3d } = data;
    return getPoiCoordinates(centerPoint3d, cadTransform);
  }, [data, deckDetails?.cadTransform]);

  const getImages = useCallback(async () => {
    const fetchedFiles = await Promise.all(
      selectedImages.map(async (file) => {
        const { fileName, resourcePath, type, name, id, imageType, uuid, caption } = file;
        const fileUrl = resourcePath ? getCloudfrontUrl(resourcePath) : '';
        const isImage =
          type.includes('image/') || [FileTypes.Jpeg, FileTypes.Png].includes(type as FileTypes);
        if (name === FileNameTypes.DeckImage && isImage && deckDetails) {
          // generate static deck plan image for media viewer
          const [width, height] = deckDetails.imageDimensions;
          const coordinates = {
            cx: location?.cx ?? 0,
            cy: location?.cy ?? 0,
          };
          const deckImageBase64 = await urlToBase64(fileUrl);
          const element = `<svg
            xmlns="http://www.w3.org/2000/svg"
            width="${width}" height="${height}"
            viewBox="0 0 ${width} ${height}"
          >
            <image href="${deckImageBase64}" width="100%" height="100%" />
            <circle cx="${coordinates.cx}" cy="${coordinates.cy}" r="20" fill="red" />
          </svg>`;

          const base64 = `data:image/svg+xml;base64,${btoa(unescape(encodeURIComponent(element)))}`;
          return {
            type,
            name: fileName,
            base64: base64,
            isNew: false,
            url: fileUrl,
            fileNameType: name,
            id: id ?? '',
          };
        }

        const base64 = isImage ? await urlToBase64(fileUrl) : '';
        return {
          type,
          name: fileName,
          base64: base64,
          isNew: false,
          url: fileUrl,
          fileNameType: name,
          id: id ?? '',
          imageType: imageType ?? undefined,
          uuid: uuid ?? undefined,
          caption: caption ?? undefined,
        };
      })
    );
    setAttachments(fetchedFiles);
  }, [deckDetails, location?.cx, location?.cy, selectedImages]);

  const handleDownloadFile = (attachmentIndex: number) => {
    const currentAttachment = attachments.filter(
      (attachment) => !attachment.imageType || attachment.imageType !== ImageType.WhiteBgPdf
    )[attachmentIndex];
    if (!currentAttachment.url) return;
    const fileUrl =
      currentAttachment.fileNameType === FileNameTypes.DeckImage
        ? currentAttachment.base64 ?? ''
        : currentAttachment.url;
    downloadFile(fileUrl, currentAttachment.name);
  };

  useEffect(() => {
    if (selectedImages.length > 0) getImages();
  }, [getImages, selectedImages]);

  const handleSaveNote = async (note: string) => {
    try {
      await updateNote(note, data.id);
      showSnackBar('Notes updated successfully', 'success');
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to update notes';
      showSnackBar(errorMessage, 'error');
      throw error;
    }
  };

  return (
    <SplitContainer data-testId="detailed-info-panel">
      <POIDetails>
        <DetailHeading>
          {data.isVerified ? POST_INSPECTION_LABEL : PRE_INSPECTION_LABEL}
        </DetailHeading>
        {data.isVerified
          ? verifiedFieldsWithValues.map((detail) => (
              <DetailBox key={detail.label}>
                <Tooltip title={detail.label}>
                  <DetailLabel>{detail.label}</DetailLabel>
                </Tooltip>
                <Tooltip title={detail.value}>
                  <DetailValue
                    data-testId={`poi-details-${detail.label.replaceAll(' ', '-').toLowerCase()}`}
                  >
                    {detail.value}
                  </DetailValue>
                </Tooltip>
              </DetailBox>
            ))
          : templateFieldsWithValues.map((detail) => (
              <DetailBox key={detail.label}>
                <Tooltip title={detail.label}>
                  <DetailLabel>{detail.label}</DetailLabel>
                </Tooltip>
                <Tooltip title={detail.value}>
                  <DetailValue
                    data-testId={`poi-details-${detail.label.replaceAll(' ', '-').toLowerCase()}`}
                  >
                    {detail.isLink ? (
                      <a
                        href={detail.value}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ color: abyssColors.primary[500] }}
                      >
                        {detail.label || detail.value}
                      </a>
                    ) : (
                      detail.value
                    )}
                  </DetailValue>
                </Tooltip>
              </DetailBox>
            ))}
      </POIDetails>
      <POIMedia>
        <NoteSection
          note={(data as MappedReportPointOfInterests).remediationNote}
          onEditClick={() => setEditNoteDialogOpen(true)}
        />
        <Grid container spacing={3}>
          <MediaViewer
            mediaFiles={attachments.filter(
              (file) => !file?.imageType || file?.imageType !== ImageType.WhiteBgPdf
            )}
            handleDownloadAttachment={handleDownloadFile}
            disableDelete
          >
            {selectedImages
              ?.filter((file) => !file?.imageType || file?.imageType !== ImageType.WhiteBgPdf)
              .map((selectedImage) => {
                const isDeckImage = selectedImage?.name === FileNameTypes.DeckImage;
                if (isDeckImage && !deckDetails) return <></>;
                const deckImage = { ...deckDetails!, centerPoint3d: data?.centerPoint3d };

                return (
                  <Grid size={{ xs: 12, md: 6 }} height="500px" key={selectedImage?.id}>
                    <RemovableReportImage
                      image={selectedImage}
                      deckImage={isDeckImage ? deckImage : undefined}
                      onRemove={(event) => {
                        event.stopPropagation();
                        handleRemove(selectedImage.resourcePath, selectedImage?.uuid);
                      }}
                      setAttachments={setAttachments}
                    />

                    <Typography fontSize={'1.1rem'} textAlign="center">
                      {truncateFilenameFromMiddle(selectedImage.caption ?? selectedImage.fileName)}.
                      {selectedImage.type.toLowerCase()}
                    </Typography>
                  </Grid>
                );
              })}
          </MediaViewer>
          {selectedPdfs.map((selectedPdf) => {
            return (
              <Grid size={{ xs: 12, md: 4 }} height="300px" key={selectedPdf?.id}>
                <RemovableReportImage
                  image={selectedPdf}
                  onRemove={(event) => {
                    event.stopPropagation();
                    handleRemove(selectedPdf.resourcePath);
                  }}
                  setAttachments={setAttachments}
                />

                <Typography fontSize={'1.1rem'} textAlign="center">
                  {truncateFilenameFromMiddle(selectedPdf.fileName)}
                </Typography>
              </Grid>
            );
          })}
        </Grid>
        <ImageUploadPanel>
          <Stack direction="row" alignItems="center" gap="24px">
            <AddImageIcon />
            <Typography fontSize="1.2rem" color={abyssColors.primary[300]}>
              You can add more images/files from different spherical or upload isometric diagram
              here
            </Typography>
          </Stack>
          <UploadButton
            onClick={() => {
              setUploadModalOpen(true);
            }}
          >
            <Add sx={{ fontSize: '1.6rem', color: abyssColors.primary[500] }} />
            <span>Add more images/files</span>
          </UploadButton>
        </ImageUploadPanel>
      </POIMedia>
      <ImageSelectionModal
        open={uploadModalOpen}
        poiDetails={data}
        disableUpload
        allPdfsOfSelectedPOI={allPdfsOfSelectedPOI}
        allImagesOfSelectedPOI={allImagesOfSelectedPOI.filter(
          (image) => !image?.imageType || image?.imageType !== ImageType.WhiteBgPdf
        )}
        handleClose={() => {
          setUploadModalOpen(false);
        }}
      />
      <EditNoteDialog
        open={editNoteDialogOpen}
        onClose={() => setEditNoteDialogOpen(false)}
        onSave={handleSaveNote}
        initialNote={(data as MappedReportPointOfInterests).remediationNote}
        title="Edit Note"
      />
    </SplitContainer>
  );
};

export const PointOfInterestInfo = ({ data }: POIInfoProps) => {
  return (
    <Stack data-testId="poi-info-panel" marginBottom={3} id={data.id}>
      <GeneralInfoPanel data={data} />
      <DetailedInfoSection data={data} />
    </Stack>
  );
};
